import { useState, useEffect, useCallback, useRef } from 'react';
import { AlertTriangle, AlertOctagon, RotateCw, X } from 'lucide-react';
import { apiFetch } from '../../lib/api/client';
import { useT } from '../../lib/i18n/I18nContext';
import { shouldPoll, nextBackoffMs, POLL_BASE_MS } from '../../lib/network/pollerBackoff.js';

// TICKET-027 sub-A — POLL_MS retained as the tick frequency; actual fetch
// gating happens inside the tick callback via shouldPoll(). Pre-fix: a 10-min
// DNS outage produced ~250 cascade console errors because every 60s tick
// blindly fired apiFetch → logAction × 4 → all hit a dead network.
const POLL_MS = POLL_BASE_MS;
const DISMISS_KEY = 'acn.providerBanner.dismissedAt';
const DISMISS_TTL_MS = 30 * 60 * 1000; // 30 min — re-shows after that so it's not forgotten forever
const KIND_LABELS = {
  credits: 'quota exhausted',
  rate_limit: 'rate-limited',
  unavailable: 'unavailable',
  timeout: 'timing out',
  auth: 'auth failure',
};

function isDismissed() {
  try {
    const ts = Number(sessionStorage.getItem(DISMISS_KEY) || 0);
    return ts && Date.now() - ts < DISMISS_TTL_MS;
  } catch { return false; }
}

export default function ProviderStatusBanner() {
  const { t } = useT();
  const [status, setStatus] = useState(null);
  const [retrying, setRetrying] = useState(false);
  const [retryResult, setRetryResult] = useState(null);
  const [dismissed, setDismissed] = useState(() => isDismissed());

  const onDismiss = useCallback(() => {
    try { sessionStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* noop */ }
    setDismissed(true);
  }, []);

  // TICKET-027 sub-A — track failures + last attempt for offline-aware backoff.
  // Refs (not state) because we don't want a re-render on every tick — only
  // when the actual status payload changes.
  const consecutiveFailuresRef = useRef(0);
  const lastAttemptAtRef = useRef(0);
  const warnedThisStreakRef = useRef(false);

  const fetchStatus = useCallback(async () => {
    lastAttemptAtRef.current = Date.now();
    try {
      const body = await apiFetch('/api/data?action=provider-status');
      if (body?.ok) setStatus(body);
      consecutiveFailuresRef.current = 0;
      warnedThisStreakRef.current = false;
    } catch (err) {
      consecutiveFailuresRef.current += 1;
      // Suppress the warn after the 1st failure of a streak — log only on
      // transitions so a sustained outage doesn't fill the console.
      if (!warnedThisStreakRef.current) {
        // Non-blocking — banner is observability, not critical path.
        console.warn('[ProviderStatusBanner] fetch failed:', err.message);
        warnedThisStreakRef.current = true;
      }
    }
  }, []);

  // Tick on POLL_BASE_MS but only fire fetchStatus when shouldPoll says yes.
  // shouldPoll hard-blocks while offline AND honors exponential backoff after
  // consecutive failures. First mount fires immediately when online.
  useEffect(() => {
    const tick = () => {
      const online = typeof navigator !== 'undefined' ? navigator.onLine !== false : true;
      const ok = shouldPoll({
        online,
        consecutiveFailures: consecutiveFailuresRef.current,
        lastAttemptAt: lastAttemptAtRef.current,
        now: Date.now(),
      });
      if (ok) fetchStatus();
    };
    tick(); // first call
    const id = setInterval(tick, POLL_MS);
    // Resume polling immediately when navigator comes back online.
    const onOnline = () => {
      // Reset backoff so the first post-recovery poll fires this tick.
      consecutiveFailuresRef.current = 0;
      lastAttemptAtRef.current = 0;
      tick();
    };
    if (typeof window !== 'undefined') {
      window.addEventListener('online', onOnline);
    }
    return () => {
      clearInterval(id);
      if (typeof window !== 'undefined') {
        window.removeEventListener('online', onOnline);
      }
    };
  }, [fetchStatus]);

  const onRetry = useCallback(async () => {
    setRetrying(true);
    setRetryResult(null);
    try {
      const body = await apiFetch('/api/data', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'voice.retry-failed-classifications', hours: 24 }),
      });
      if (body?.ok) {
        setRetryResult(`Retried ${body.found} — ${body.succeeded} succeeded, ${body.failed} still failed.`);
        fetchStatus();
      } else {
        setRetryResult('Retry failed — check logs.');
      }
    } catch (err) {
      setRetryResult(`Retry error: ${err.message?.slice(0, 80)}`);
    } finally {
      setRetrying(false);
    }
  }, [fetchStatus]);

  if (!status || status.overall === 'ok' || dismissed) return null;

  const incident = status.last_incident;
  const incidentKind = incident?.kind ? (KIND_LABELS[incident.kind] || incident.kind) : 'degraded';
  const isCritical = status.overall === 'critical';

  const message = isCritical
    ? `All AI providers unavailable — Gemini ${incidentKind}, OpenAI also failing. Voice classification is paused.`
    : `Voice classifier running on OpenAI fallback — Gemini ${incidentKind}. New recordings still process normally.`;

  const containerStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 50,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    padding: '8px 16px',
    // Banner fills behind the Dynamic Island but its content (incl. Retry)
    // sits below it (viewport-fit=cover).
    paddingTop: 'calc(8px + env(safe-area-inset-top, 0px))',
    fontSize: 13,
    fontWeight: 500,
    color: 'white',
    background: isCritical ? 'var(--bad)' : 'var(--warn)',
    flexWrap: 'wrap',
  };

  const buttonStyle = {
    minHeight: 32,
    padding: '4px 12px',
    fontSize: 12,
    fontWeight: 600,
    borderRadius: 6,
    border: 'none',
    cursor: retrying ? 'default' : 'pointer',
    background: 'rgba(255,255,255,0.22)',
    color: 'white',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    opacity: retrying ? 0.6 : 1,
  };

  return (
    <div data-testid="provider-status-banner" style={containerStyle} role="status">
      {isCritical ? <AlertOctagon size={16} /> : <AlertTriangle size={16} />}
      <span>{message}</span>
      {retryResult && <span style={{ fontWeight: 400, opacity: 0.9 }}>{retryResult}</span>}
      <button
        type="button"
        onClick={onRetry}
        disabled={retrying}
        style={buttonStyle}
        data-testid="provider-status-retry"
      >
        <RotateCw size={12} className={retrying ? 'animate-spin' : ''} />
        {retrying ? 'Retrying…' : 'Retry failed'}
      </button>
      <button
        type="button"
        onClick={onDismiss}
        aria-label={t('providerBanner.dismiss')}
        data-testid="provider-status-dismiss"
        style={{
          minHeight: 32,
          minWidth: 32,
          padding: 4,
          borderRadius: 6,
          border: 'none',
          cursor: 'pointer',
          background: 'rgba(255,255,255,0.15)',
          color: 'white',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <X size={14} />
      </button>
    </div>
  );
}
